import axios, { AxiosResponse } from "axios";
import { PageProps } from "gatsby";
import React, { FC, useCallback, useEffect, useState } from "react";
import { useForm } from "react-hook-form";

import { useReCaptchaButton } from "../../captcha/use-recaptcha-button";
import { Button } from "../../components/buttons/button";
import { SearchResultItemCard } from "../../components/cards/search-result-item-card.tsx/search-result-item-card";
import { Footer } from "../../components/footer";
import { Input } from "../../components/forms/elements/input";
import { HeaderMenuSection } from "../../components/header-menu";
import { Icon } from "../../components/icon";
import { Spinner } from "../../components/spinner";
import { HEADER_SOURCE, QS_QUERY_KEY, QS_SOURCE_KEY } from "../../constants/search";
import { useLabels } from "../../contexts/labels";
import { useGoogleSearchAPI } from "../../hooks/search";
import { useDataLayer } from "../../hooks/use-data-layer";
import { useTrackPageview } from "../../hooks/use-track-pageview";
import { ContentGroup } from "../../types/analytics";
import {
  GoogleAnalyticsEvent,
  NonEditorialPagesBreadcrumbs,
  SearchResults,
} from "../../utils/analytics";
import { getCountryLanguageFromLocaleCode } from "../../utils/i18n";
import { StandardPageContext } from "../standard-page";

interface SearchFormProps {
  search: string;
}

export type SearchPageContext = Omit<StandardPageContext, "id">;

export interface SearchPageBodyProps
  extends PageProps<Queries.SearchPageQuery, SearchPageContext> {}

const SEARCH_INPUT_ID = "search-input";
const isCaptchaDisabled = process.env.GATSBY_DISABLE_CAPTCHA_ON_SEARCH === "true";

export const SearchPageBody: FC<SearchPageBodyProps> = ({
  data: { allContentfulHeaderMenu, allContentfulFooter },
  pageContext: { locale },
}) => {
  const searchLanguage = getCountryLanguageFromLocaleCode(locale).language;
  const contentfulHeaderMenu = allContentfulHeaderMenu.nodes.find((x) => x.node_locale === locale);
  const contentfulFooter = allContentfulFooter.nodes.find((x) => x.node_locale === locale);
  const { labels } = useLabels();

  const { search, isLoading: isLoadingSearch, result } = useGoogleSearchAPI();

  const { pushEvent } = useDataLayer();
  const trackPageview = useTrackPageview();
  useEffect(() => {
    trackPageview({
      breadcrumbs: [
        {
          label: NonEditorialPagesBreadcrumbs.Search,
          defaultLocaleLabel: NonEditorialPagesBreadcrumbs.Search,
          url: "",
        },
      ],
      content_group: ContentGroup.Other,
      locale,
    });
  }, [trackPageview, locale]);

  const { register, setValue, handleSubmit, watch } = useForm<SearchFormProps>();
  const searchValue = watch("search");
  const [lastSearchedValue, setLastSearchedValue] = useState("");

  const [tokenVerificationIsError, setTokenVerificationIsError] = useState<boolean | null>(null);

  const { handleReCaptchaVerify, isDisabled: isReCaptchaNotReady } = useReCaptchaButton("search");

  const isLoading = isLoadingSearch || isReCaptchaNotReady || tokenVerificationIsError === null;

  useEffect(() => {
    (async () => {
      if (!isCaptchaDisabled) {
        if (isReCaptchaNotReady) return;
        const token = await handleReCaptchaVerify();
        if (!token) {
          return setTokenVerificationIsError(true);
        }
        const response = await axios.post<
          never,
          AxiosResponse<{ status: string; message: string }>
        >(
          "/.netlify/functions/verify-captcha",
          { token },
          {
            validateStatus: () => true, // no exceptions are thrown if status != 200
          }
        );
        if (response.data.status !== "success") {
          return setTokenVerificationIsError(true);
        } else {
          setTokenVerificationIsError(false);
        }
      }
      const q = new URLSearchParams(window.location.search).get(QS_QUERY_KEY) || "";
      setLastSearchedValue(q);
      setValue("search", q);
      search(q, searchLanguage);
    })();
  }, [searchLanguage, search, setValue, isReCaptchaNotReady, handleReCaptchaVerify]);

  const onSubmit = useCallback(({ search }: SearchFormProps) => {
    if (typeof window !== "undefined" && search) {
      const searchParams = new URLSearchParams(window.location.search);
      searchParams.set(QS_QUERY_KEY, search);
      searchParams.set(QS_SOURCE_KEY, HEADER_SOURCE);
      window.location.search = searchParams.toString();
    }
  }, []);

  const resetSearch = useCallback(() => {
    setValue("search", "");
    try {
      document.getElementById(SEARCH_INPUT_ID)?.focus();
    } catch (e) {
      console.error(e);
    }
  }, [setValue]);

  useEffect(() => {
    if (result) {
      const success = "items" in result ? result.items.length > 0 : false;
      pushEvent({
        event: GoogleAnalyticsEvent.Search,
        search_term: lastSearchedValue,
        search_result: success ? SearchResults.Results : SearchResults.NoResults,
      });
    }
  }, [result, lastSearchedValue, pushEvent]);

  return (
    <>
      {contentfulHeaderMenu && (
        <HeaderMenuSection
          headerMenu={contentfulHeaderMenu}
          internalPagePath="/search"
          pageContentfulId={null}
        />
      )}
      <div className="flex flex-col mx-auto px-8 lg:px-20 py-16 container max-w-5xl gap-12">
        <div className="text-primary text-5xl font-medium leading-tight self-center">
          {labels["search-title"]}
        </div>
        <form onSubmit={handleSubmit(onSubmit)} className="flex flex-row gap-2 items-center">
          <Input
            type="text"
            className="flex-grow"
            id={SEARCH_INPUT_ID}
            placeholder={labels["search-modal-form-placeholder"] ?? undefined}
            {...register("search")}
            endDecoration={
              searchValue ? (
                <Icon icon="close" size={25} onClick={resetSearch} />
              ) : (
                <Icon icon="search" size={25} />
              )
            }
          />
          <Button
            variant="primary"
            size="sm"
            type="submit"
            disabled={
              !isCaptchaDisabled && (isReCaptchaNotReady || tokenVerificationIsError !== false)
            }
          >
            {labels["header-menu-search-submit"]}
          </Button>
        </form>
        {result?.items?.length ? (
          result.items.map(({ htmlTitle, link, htmlSnippet }) => (
            <SearchResultItemCard
              key={link}
              title={htmlTitle}
              url={link}
              snippet={htmlSnippet}
              ctaLabel={labels["search-cta-label"]}
            />
          ))
        ) : (
          <div className="mx-auto my-12">
            {isLoading ? (
              <Spinner size="large" />
            ) : tokenVerificationIsError ? (
              <div className="text-dark text-xl">{labels["captcha-verification-failed"]}</div>
            ) : lastSearchedValue ? (
              <div className="text-dark text-xl">
                {labels["search-empty-state"]} <strong>&quot;{lastSearchedValue}&quot;</strong>
              </div>
            ) : (
              <div className="text-dark text-xl">{labels["search-no-search-issued"]}</div>
            )}
          </div>
        )}
      </div>
      {contentfulFooter && <Footer {...contentfulFooter} />}
    </>
  );
};
